import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaWhatsapp } from 'react-icons/fa';
import { LuMail, LuLightbulb, LuCheckCircle, LuX } from 'react-icons/lu';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  box-shadow: ${props => props.theme.shadows.xl};
  animation: ${slideUp} 0.2s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    max-width: 95%;
  }
`;

const ModalHeader = styled.div`
  background: ${props => props.$isWhatsApp ? '#059669' : props.theme.colors.primary};
  padding: 28px 24px;
  text-align: center;
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: ${props => props.theme.colors.white};
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.colors.white};
  font-size: 22px;
  font-weight: 700;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  margin: 6px 0 0 0;
`;

const ModalBody = styled.div`
  padding: 28px 24px;
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  padding: 18px;
  border-radius: 8px;
  margin-bottom: 18px;
`;

const InfoTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 10px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 14px 0;
`;

const ContactInfo = styled.div`
  background: ${props => props.theme.colors.white};
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.type === 'whatsapp' ? '#059669' : props.theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px dashed ${props => props.type === 'whatsapp' ? '#10b981' : props.theme.colors.secondary};

  svg {
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 12px 14px;
  }
`;

const AlertBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 18px;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #b45309;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 22px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 13px 24px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 20px;
  }
`;

const ConfirmButton = styled(Button)`
  background: #059669;
  color: white;

  &:hover:not(:disabled) {
    background: #047857;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.textSecondary};
  border: 1px solid ${props => props.theme.colors.border};

  &:hover {
    background: ${props => props.theme.colors.grayLight};
  }
`;

const ConfirmationModal = ({ open, onConfirm, onCancel, contactType, contactValue, isLoading = false }) => {
  const isWhatsApp = contactType === 'whatsapp';

  return (
    <ModalOverlay open={open}>
      <ModalContainer>
        <ModalHeader $isWhatsApp={isWhatsApp}>
          <IconWrap>
            {isWhatsApp ? <FaWhatsapp size={28} /> : <LuMail size={28} />}
          </IconWrap>
          <ModalTitle>Confirmar Contratação</ModalTitle>
          <ModalSubtitle>
            {isWhatsApp ? 'Confirmação via WhatsApp' : 'Confirmação via E-mail'}
          </ModalSubtitle>
        </ModalHeader>

        <ModalBody>
          <InfoBox>
            <InfoTitle>
              {isWhatsApp ? 'Confirmação via WhatsApp' : 'Confirmação via E-mail'}
            </InfoTitle>

            <InfoText>
              {isWhatsApp ? (
                <>
                  Enviaremos uma mensagem via <strong>WhatsApp</strong> com o link de confirmação
                  para continuar sua contratação. Fique atento ao seu celular.
                </>
              ) : (
                <>
                  Enviaremos um <strong>e-mail</strong> com o link de confirmação
                  para continuar sua contratação. Verifique também sua caixa de spam.
                </>
              )}
            </InfoText>

            <ContactInfo type={isWhatsApp ? 'whatsapp' : 'email'}>
              {isWhatsApp ? <FaWhatsapp size={20} /> : <LuMail size={20} />}
              {contactValue}
            </ContactInfo>
          </InfoBox>

          <AlertBox>
            <LuLightbulb size={20} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', color: '#92400e', lineHeight: '1.6' }}>
                <strong>Atenção:</strong> Certifique-se de que o {isWhatsApp ? 'número de WhatsApp' : 'e-mail'} está
                correto. Você receberá todas as instruções para finalizar sua contratação através deste contato.
              </div>
            </div>
          </AlertBox>

          <ButtonContainer>
            <CancelButton onClick={onCancel} disabled={isLoading}>
              <LuX size={20} />
              Cancelar
            </CancelButton>
            <ConfirmButton onClick={onConfirm} disabled={isLoading}>
              <LuCheckCircle size={20} />
              {isLoading ? 'Validando...' : 'Confirmar'}
            </ConfirmButton>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ConfirmationModal;
